import React from 'react';
import ConfirmDialog from '@parsina/framework/clinical/src/components/common/ConfirmDialog';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';

// ─────────────────────────────────────────────────────────────────────────────
// «تغییرات ذخیره‌نشده» — the guard on dismissing a form that has been typed in.
//
// ── What this fixes ─────────────────────────────────────────────────────────
// Every form sheet in the app closed UNCONDITIONALLY: `onClose` reset the form
// and the sheet went away, whether the doctor had typed one character or filled
// in a whole medication. On a desktop that costs a mis-click; on a phone the
// sheet has FOUR dismissals — the ✕, the scrim, Escape, and (since overlays
// joined history) the platform back button — and the last one is an edge-swipe
// a thumb performs by accident while scrolling.
//
// The confirm is deliberately NOT on the sheet's submit path and NOT on an
// untouched form: a doctor who opened a sheet by mistake must still be able to
// shut it in one tap. Only actual typed content earns a question.
//
// `ConfirmDialog` is Radix at z-1100 — above the sheet's 1000 — which is what
// makes this legible over the very sheet it is asking about.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @param {boolean}  dirty    has the form been changed?
 * @param {Function} onClose  what an unguarded close would have done
 *
 * @returns {{ requestClose, asking, guard }}
 *   `requestClose` replaces `onClose` at every dismissal site;
 *   `guard` is the dialog element to render inside the sheet.
 */
export function useDirtyClose(dirty, onClose) {
  const [asking, setAsking] = React.useState(false);

  // The flags are read through refs so `requestClose` keeps one identity for
  // the life of the sheet: it is handed to `BottomSheet`'s `onClose`, and a
  // changing identity there re-runs the sheet's own history and focus effects
  // on every keystroke.
  const dirtyRef = React.useRef(dirty);
  dirtyRef.current = dirty;
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;

  const requestClose = React.useCallback(() => {
    if (!dirtyRef.current) { onCloseRef.current?.(); return; }
    setAsking(true);
  }, []);

  const discard = React.useCallback(() => {
    setAsking(false);
    onCloseRef.current?.();
  }, []);

  return {
    requestClose,
    asking,
    guard: <DirtyCloseGuard open={asking} onDiscard={discard} onKeepEditing={() => setAsking(false)} />,
  };
}

export default function DirtyCloseGuard({ open, onDiscard, onKeepEditing }) {
  const { t } = useTranslation();
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={(next) => { if (!next) onKeepEditing?.(); }}
      title={t('common.unsaved.title')}
      message={t('common.unsaved.body')}
      confirmLabel={t('common.unsaved.discard')}
      cancelLabel={t('common.unsaved.keepEditing')}
      destructive
      onConfirm={onDiscard}
    />
  );
}
